import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Link } from 'wouter'
import { ArrowLeft, Shield, Eye, Database, Lock, Users, Bell, FileText } from 'lucide-react'

export function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link href="/login">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Login
            </Button>
          </Link>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-xl bg-gradient-to-r from-primary to-primary/80 mb-6">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Privacy Policy</h1>
            <p className="text-xl text-muted-foreground">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Privacy Content */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">1. Information We Collect</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              We collect information you provide directly to us, such as when you create an account, create or respond to RFPs, 
              or contact our support team. This may include:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">Personal Information</span>
              </div>
              <div className="flex items-center space-x-3">
                <Database className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">Account Data</span>
              </div>
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">RFP Content</span>
              </div>
              <div className="flex items-center space-x-3">
                <Bell className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">Communication History</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">2. How We Use Your Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              We use the information we collect to provide, maintain, and improve our services, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Providing and managing your account</li>
              <li>Processing RFP submissions and responses</li>
              <li>Connecting you with relevant suppliers or procurement professionals</li>
              <li>Improving our AI-powered content generation features</li>
              <li>Sending important service updates and notifications</li>
              <li>Providing customer support</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">3. Information Sharing and Disclosure</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, 
              except in the following circumstances:
            </p>
            <div className="bg-muted/50 rounded-lg p-4 mt-4">
              <h4 className="font-medium mb-2">Limited Sharing for Service Delivery:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>RFP content may be shared with relevant suppliers (without personal contact details)</li>
                <li>Service providers who assist in operating our platform</li>
                <li>Legal requirements or to protect our rights and safety</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">4. Data Security</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal information 
              against unauthorized access, alteration, disclosure, or destruction.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="flex items-center space-x-3">
                <Lock className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">Encryption</span>
              </div>
              <div className="flex items-center space-x-3">
                <Shield className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">Access Controls</span>
              </div>
              <div className="flex items-center space-x-3">
                <Eye className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">Regular Audits</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">5. Data Retention</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              We retain your personal information for as long as necessary to provide our services and fulfill the purposes 
              outlined in this Privacy Policy. You may request deletion of your account and associated data at any time.
            </p>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> Some information may be retained for legal, regulatory, or legitimate business purposes 
                even after account deletion.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">6. Your Rights and Choices</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              You have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Access and review your personal information</li>
              <li>Update or correct inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt-out of certain communications</li>
              <li>Control cookie preferences</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              To exercise these rights, please contact us using the information provided below.
            </p>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">7. Cookies and Tracking Technologies</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              We use cookies and similar tracking technologies to enhance your experience, analyze usage patterns, 
              and provide personalized content. You can control cookie settings through your browser preferences.
            </p>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">8. Contact Us</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="font-medium">SourcedIn Data Protection Officer</p>
              <p className="text-sm text-muted-foreground">Email: privacy@sourcedin.com</p>
              <p className="text-sm text-muted-foreground">Phone: +1 (555) 123-4567</p>
              <p className="text-sm text-muted-foreground">Address: 123 Business St, Suite 100, City, State 12345</p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-8">
          <Link href="/login">
            <Button>
              Return to Login
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}
